import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { RouterModule, NavigationStart, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { Loading } from '@domain/models/alert/loading.model';
import { BrandService } from '@infraestructure/drive-adapter/brand-service/brand.service';
import { Alert } from '@domainModels/alert/alert.model';
import { AlertPosition, AlertType } from '@domain/enums/alert/alert-type.enum';
import { AlertService } from '@infraestructureDriveAdapter/alert-service/alert.service';
import { LoadingType } from '@domain/enums/alert/loading-type.enum';
import { CommonModule } from '@angular/common';
import { BrandResource, BrandResources } from '@domain/models/brand/brand-resources.model';

@Component({
  selector: 'app-alert',
  templateUrl: 'alert.component.html',
  styleUrls: ['alert.component.scss'],
  standalone: true,
  imports: [CommonModule, RouterModule],
})
export class AlertComponent implements OnInit, OnDestroy {
  @Input() id: string = 'default-alert';
  public alerts: Array<Alert> = [];
  public showAsModal?: boolean;
  public loading?: Loading;
  public alertType = AlertType;
  public LoadingType = LoadingType;
  public brandResource!: BrandResource;
  private _alertSubscription!: Subscription;
  private _loadingSubscription!: Subscription;
  private _routeSubscription!: Subscription;

  constructor(
    private _router: Router,
    private _alertService: AlertService,
    private _brandService: BrandService
  ) {}

  ngOnInit() {
    this._brandService.getSelectedBrandResources().subscribe((brandResource: BrandResources) => {
      this.brandResource = brandResource?.brandResource!;
    });
    this._alertSubscription = this._alertService.onAlert(this.id).subscribe((alertToShow: Alert) => {
      if (!alertToShow.description) {
        this.removeAlert(alertToShow);
        return;
      }
      const alertTypeTitle = {
        [AlertType.InfoLogo]: '',
        [AlertType.Success]: 'SUCCESS',
        [AlertType.Error]: 'Oops...',
        [AlertType.Info]: 'INFORMATION',
        [AlertType.Warning]: 'WARNING',
      };

      this.showAsModal = alertToShow.showAsModal;
      if (!alertToShow?.title) alertToShow!.title = alertTypeTitle[alertToShow.type!];

      if (!this.alerts.some((c) => c.description === alertToShow.description)) this.alerts.push(alertToShow);

      if (alertToShow.autoClose) {
        setTimeout(() => this.removeAlert(alertToShow), 5000);
      }
    });

    this._loadingSubscription = this._alertService.onLoading().subscribe((loading: Loading) => {
      this.loading = loading;
    });

    // clear alerts on location change
    this._routeSubscription = this._router.events.subscribe((event: any) => {
      if (event instanceof NavigationStart) {
        this._alertService.hideAlert(this.id);
      }
    });
  }

  ngOnDestroy() {
    // unsubscribe to avoid memory leaks
    this._alertSubscription?.unsubscribe();
    this._loadingSubscription?.unsubscribe();
    this._routeSubscription?.unsubscribe();
  }

  removeAlert(alert: Alert) {
    if (!this.alerts.find((alert) => alert.id == alert.id)) return;
    this.alerts = this.alerts.filter((x: Alert) => x.id !== alert.id);
  }

  runFunctionOnBtnClick(alert: Alert) {
    if (alert.runFunctionOnBtnClick) {
      alert.runFunctionOnBtnClick();
    }
    this.removeAlert(alert);
  }

  runFunctionOnClose(alert: Alert) {
    if (alert.runFunctionOnClose) {
      alert.runFunctionOnClose();
    }
    this.removeAlert(alert);
  }

  cssClasses(alert: Alert) {
    if (!alert) return;
    const classes = ['alert', 'alert-dismissable'];
    //Define type:
    const alertTypeClass = {
      [AlertType.InfoLogo]: 'alert-light',
      [AlertType.Success]: 'alert-success',
      [AlertType.Error]: 'alert-danger',
      [AlertType.Info]: 'alert-info',
      [AlertType.Warning]: 'alert-warning',
    };
    classes.push(alertTypeClass[alert.type!]);
    //Define position:
    const alertPositionClass = {
      [AlertPosition.TopLeft]: 'top-left',
      [AlertPosition.TopCenter]: 'top-center',
      [AlertPosition.TopRight]: 'top-right',
      [AlertPosition.BottomLeft]: 'bottom-left',
      [AlertPosition.BottomRight]: 'bottom-right',
    };

    if (alert.showAsModal) classes.push(alertPositionClass[AlertPosition.TopCenter]);
    else classes.push(alertPositionClass[alert.position!]);

    return classes.join(' ');
  }
}
